import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import ServiceUnavailable from '@/pages/ServiceUnavailable';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading, isSuperAdmin, checkingSuperAdmin, memberships, membershipsError, membershipsErrorStatus, loadingMemberships, superAdminErrorStatus } = useAuth();
  const location = useLocation();
  const [verifiedSuperAdmin, setVerifiedSuperAdmin] = useState<boolean | null>(null);
  const [superAdminVerifyErrorStatus, setSuperAdminVerifyErrorStatus] = useState<number | null>(null);

  // Use the super admin status from AuthContext (already verified server-side)
  // AuthContext handles the server-side verification, so we don't need to duplicate it here
  // This prevents multiple unnecessary calls to check-super-admin
  useEffect(() => {
    if (!user) {
      setVerifiedSuperAdmin(null);
      setSuperAdminVerifyErrorStatus(null);
      return;
    }

    // If AuthContext is still checking, wait
    if (checkingSuperAdmin) {
      return;
    }

    // Use the result from AuthContext - it already does server-side verification
    // We only need to track the verification state for UI purposes
    if (isSuperAdmin) {
      // Super admin confirmed by AuthContext (which already verified server-side)
      setVerifiedSuperAdmin(true);
      setSuperAdminVerifyErrorStatus(superAdminErrorStatus); // Track any errors from AuthContext
    } else {
      // Not a super admin (confirmed by AuthContext)
      setVerifiedSuperAdmin(false);
      setSuperAdminVerifyErrorStatus(superAdminErrorStatus); // Track any errors from AuthContext
    }
  }, [user, isSuperAdmin, checkingSuperAdmin, superAdminErrorStatus]);

  // Wait for auth to load (including super admin check from AuthContext)
  if (loading || checkingSuperAdmin || loadingMemberships) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check for service unavailable errors (503, 500) - show service down page
  // Check both super admin and memberships errors
  const serviceUnavailableStatus = membershipsErrorStatus === 503 || 
                                    membershipsErrorStatus === 500 ||
                                    superAdminErrorStatus === 503 ||
                                    superAdminErrorStatus === 500 ||
                                    superAdminVerifyErrorStatus === 503 ||
                                    superAdminVerifyErrorStatus === 500;
  
  // Check for unauthorized errors (401) - redirect to login
  const unauthorizedStatus = membershipsErrorStatus === 401 ||
                              superAdminErrorStatus === 401 ||
                              superAdminVerifyErrorStatus === 401;
  
  if (unauthorizedStatus) {
    console.warn('🔒 Unauthorized (401) detected, redirecting to login:', {
      membershipsErrorStatus,
      superAdminErrorStatus,
      superAdminVerifyErrorStatus
    });
    // Clear session and redirect to login
    return <Navigate to="/login" state={{ from: location, reason: 'unauthorized' }} replace />;
  }
  
  if (serviceUnavailableStatus) {
    console.error('🚨 Service unavailable detected:', {
      membershipsErrorStatus,
      superAdminErrorStatus,
      superAdminVerifyErrorStatus
    });
    return (
      <ServiceUnavailable 
        message="Our services are temporarily unavailable. This might be due to scheduled maintenance or a temporary issue."
        onRetry={() => window.location.reload()}
      />
    );
  }

  // Super admins can access everything (verified server-side)
  // Security: Even if someone modifies frontend state, server verification prevents unauthorized access
  // Note: Super admins don't need league memberships - they can access everything
  // IMPORTANT: Check isSuperAdmin BEFORE checking memberships to prevent false "No League Membership" errors
  if (isSuperAdmin) {
    // If verification succeeded, allow access
    if (verifiedSuperAdmin === true) {
      return <>{children}</>;
    }
    
    // If verification explicitly failed, deny access (security check)
    if (verifiedSuperAdmin === false) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-md text-center">
            <h2 className="text-xl font-bold mb-2 text-red-600">Access Denied</h2>
            <p className="text-stone-600">
              Unauthorized access attempt detected.
            </p>
          </div>
        </div>
      );
    }
    
    // Still verifying or timeout - allow access with grace period
    // This handles cases where verification is slow (e.g., during upgrades)
    // The timeout in useEffect will set verifiedSuperAdmin to true after 3 seconds
    // Allow access while verifying (timeout will catch failures)
    console.log('🔍 ProtectedRoute: Super admin detected, allowing access while verification completes');
    return <>{children}</>;
  }

  // Regular users need a membership
  // Only check this if user is NOT a super admin
  // IMPORTANT: If there's an error loading memberships, treat it as a connectivity issue, not "no leagues"
  if (membershipsError) {
    console.error('⚠️ ProtectedRoute: Error loading memberships (connectivity issue):', membershipsError);
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md text-center">
          <h2 className="text-xl font-bold mb-2 text-amber-600">Connection Issue</h2>
          <p className="text-stone-600 mb-4">
            Unable to load your league memberships. This might be due to a temporary connectivity issue or database maintenance.
          </p>
          <p className="text-sm text-stone-500">
            Please try refreshing the page in a moment.
          </p>
        </div>
      </div>
    );
  }

  // Only check for "No League Membership" if we successfully loaded memberships and found none
  // IMPORTANT: Don't show "No League Membership" if we're still verifying or if verification just failed
  // This prevents showing misleading errors during database upgrades or network issues
  if (memberships.length === 0) {
    // If we're still checking super admin, wait
    if (checkingSuperAdmin) {
      console.log('⏳ ProtectedRoute: Waiting for super admin check to complete');
      return (
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
        </div>
      );
    }
    
    // Only show "No League Membership" if verification is complete and user is confirmed not a super admin
    // verifiedSuperAdmin === false means we checked and user is NOT a super admin
    // verifiedSuperAdmin === null means we haven't checked yet (shouldn't happen here, but be safe)
    if (verifiedSuperAdmin === false || (verifiedSuperAdmin === null && !isSuperAdmin)) {
      console.log('⚠️ ProtectedRoute: No memberships found for regular user, redirecting to /register');
      return <Navigate to="/register" replace />;
    }
    
    // If verifiedSuperAdmin is null and isSuperAdmin is true, we should have handled this above
    // But just in case, show loading
    console.log('⏳ ProtectedRoute: Waiting for verification to complete');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    );
  }

  return <>{children}</>;
}
